// Amos-83 Request Portal — the requester's own submissions, newest first.
// Amos-104: Not-found entries are no longer rows here (shown once, on the submit confirmation); a Sent row
// reads "Sent <date> to <To> — accepted by our mail server" (§d).
import React, { useCallback, useEffect, useState } from 'react';
import { COPY, STATUS_LABEL } from './copy';
import { portalApi, PortalApiError, StatusRow } from './api';

export function statusClass(status: string): string {
  switch (status) {
    case 'sent': return 'bg-green-50 text-green-700 border-green-200';
    case 'in_review': return 'bg-blue-50 text-blue-700 border-blue-200';
    case 'closed': return 'bg-slate-100 text-slate-600 border-slate-200';
    case 'not_found': return 'bg-amber-50 text-amber-800 border-amber-200';
    default: return 'bg-slate-50 text-slate-700 border-slate-200';
  }
}

export function fmtDate(iso?: string): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString(undefined, { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
}

/** Date only, for the repeat warning and the Sent line. */
export function fmtDay(iso?: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
}

/** Merge request rows (and, before Amos-104, not-found entries) into one list, newest first. */
export function mergeRows(items: StatusRow[], notFound: StatusRow[]): StatusRow[] {
  return [...items, ...notFound].sort((a, b) => (b.submitted_at || '').localeCompare(a.submitted_at || ''));
}

export default function StatusTable({ onSessionExpired }: { onSessionExpired: () => void }) {
  const [rows, setRows] = useState<StatusRow[]>([]);
  const [cursor, setCursor] = useState<string | null>(null);
  const [state, setState] = useState<'loading' | 'ready' | 'error'>('loading');

  const load = useCallback(async (next?: string | null) => {
    setState('loading');
    try {
      const r = await portalApi.list(next);
      setRows((prev) => (next ? mergeRows([...prev, ...r.items], []) : mergeRows(r.items, [])));
      setCursor(r.next_cursor);
      setState('ready');
    } catch (e) {
      if ((e as PortalApiError).status === 401) { onSessionExpired(); return; }
      setState('error');
    }
  }, [onSessionExpired]);

  useEffect(() => { load(null); }, [load]);

  return (
    <section className="bg-white rounded-lg shadow-sm border border-slate-200 p-6" data-testid="portal-status">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h2 className="text-lg font-semibold text-slate-800">{COPY.statusHeading}</h2>
          <p className="text-sm text-slate-600 mt-1">{COPY.statusIntro}</p>
        </div>
        <button onClick={() => load(null)} disabled={state === 'loading'} className="text-sm text-blue-700 hover:underline disabled:text-slate-400">{COPY.statusRefresh}</button>
      </div>

      {state === 'loading' && rows.length === 0 && <p className="text-sm text-slate-500 mt-6" data-testid="portal-status-loading">{COPY.statusLoading}</p>}
      {state === 'error' && (
        <div role="alert" className="text-red-700 text-sm bg-red-50 border border-red-200 rounded-md p-3 mt-4" data-testid="portal-status-error">
          {COPY.statusError} <button onClick={() => load(null)} className="underline">{COPY.statusRefresh}</button>
        </div>
      )}
      {state === 'ready' && rows.length === 0 && (
        <div className="text-center py-12" data-testid="portal-status-empty">
          <p className="text-slate-700 font-medium">{COPY.statusEmpty}</p>
          <p className="text-sm text-slate-500 mt-1">{COPY.statusEmptyHint}</p>
        </div>
      )}
      {rows.length > 0 && (
        <div className="overflow-x-auto mt-4">
          <table className="min-w-full text-sm">
            <thead>
              <tr className="text-left text-xs uppercase tracking-wide text-slate-500 border-b border-slate-200">
                <th className="py-2 pr-4">{COPY.colPolicy}</th>
                <th className="py-2 pr-4">{COPY.colInsured}</th>
                <th className="py-2 pr-4">{COPY.colSubmitted}</th>
                <th className="py-2 pr-4">{COPY.colStatus}</th>
                <th className="py-2">{COPY.colUpdated}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {rows.map((r) => (
                <tr key={r.request_id || `${r.ref}-${r.submitted_at}`} className={r.status === 'not_found' ? 'text-slate-500' : ''}>
                  <td className="py-2 pr-4 font-mono text-slate-800">{r.ref}</td>
                  <td className="py-2 pr-4 text-slate-700 max-w-xs truncate">
                    {r.status === 'not_found'
                      ? <span className="italic">{r.reason === 'rejected_shape' ? COPY.statusReasonBadShape : COPY.statusReasonNotInList}</span>
                      : (r.insured_name || '—')}
                  </td>
                  <td className="py-2 pr-4 whitespace-nowrap">{fmtDate(r.submitted_at)}</td>
                  <td className="py-2 pr-4">
                    <span title={COPY.statusHelp[r.status] || ''} className={`inline-flex px-2 py-0.5 rounded text-xs font-medium border ${statusClass(r.status)}`}>
                      {STATUS_LABEL[r.status] || r.status}
                    </span>
                    {r.status === 'sent' && (r.sent_to || []).length > 0 && (
                      <span className="block text-xs text-slate-600 mt-1" data-testid="portal-sent-detail">
                        {COPY.statusSentDetail(fmtDay(r.sent_at || r.status_at), (r.sent_to || []).join(', '))}
                      </span>
                    )}
                  </td>
                  <td className="py-2 whitespace-nowrap">{fmtDate(r.status_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {cursor && (
            <div className="mt-3">
              <button onClick={() => load(cursor)} disabled={state === 'loading'} className="text-sm text-blue-700 hover:underline">{COPY.statusLoadMore}</button>
            </div>
          )}
          <p className="text-xs text-slate-500 mt-4">
            {Object.entries(COPY.statusHelp).map(([k, v]) => (
              <span key={k} className="block"><span className="font-medium">{STATUS_LABEL[k]}:</span> {v}</span>
            ))}
          </p>
        </div>
      )}
    </section>
  );
}
